import { Checkbox, Input, Scope } from 'informed';
import { useState } from 'react';
import TymberInfoBox from '../TymberInfoBox';
import classNames from 'classnames';
import { ActionIcon, ContentIcon, FavouriteIcon } from '@tymbe/ui-icons';
import { ApplicationData } from '../../../../types/TymbeApi';
import moment from 'moment';
import ThumbsUp from '../ThumbsUp';
import ThumbsDown from '../ThumbsDown';
import {
  AttendanceResolution,
  AttendanceResolutionDisplay,
} from '../../../../utils/enums';
import DropDownMenu from '../ShiftDetail/DropDownMenu';
import { TyInput } from '@tymbe/ui-forms';
import {
  BAD_JOB_EVALUATION_SCORE,
  GOOD_JOB_EVALUATION_SCORE,
} from '../../../../utils/constants';
import { calculateDurationFromWorkTime } from '../../../../utils/time';
import feathersClient from '../../../../apiClient';
import { useMutation } from 'react-query';
import queryClient from '../../../../queryClient';
import BlockTymber from './BlockTymber';
import Modal from 'react-modal';
import { ErrorAlert, SuccessAlert } from '../../../../components/alerts';
import Spinner from '@tymbe/ty-components/indicators/Spinner';

interface ResolvedAttendanceRowProps {
  application: ApplicationData;
}

const ResolvedAttendanceRow = ({ application }: ResolvedAttendanceRowProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { mutateAsync: deleteAttendance, isLoading } = useMutation(
    ['deleteAttendance'],
    async (attendanceId: number) => {
      await feathersClient.service('attendance').remove(attendanceId);
    }
  );
  const onDeleteAttendance = async (attendanceId?: number): Promise<void> => {
    if (!attendanceId) return;
    try {
      await deleteAttendance(attendanceId);
      SuccessAlert(`Docházka byla úspěšně smazána`);
    } catch (error) {
      ErrorAlert(`Při pokusu o smazání docházy došlo k chybě`);
    }

    queryClient.invalidateQueries(['shift']);
  };

  const menuOptions = [
    {
      text: <span className="text-red-700">Ukončit spolupráci</span>,
      onClick: () => setModalOpen(true),
    },
  ];

  return (
    <Scope scope={`${application.id}`}>
      <div className="grid grid-cols-[25%_auto_40%] gap-4 p-4">
        <div className="flex items-center gap-2">
          <Input
            name="resolved_id"
            required
            type="hidden"
            initialValue={application.id}
          />
          <Checkbox
            className="size-[18px] rounded"
            name="resolved_attendance"
            initialValue={true}
            disabled
          />
          <TymberInfoBox personId={application.person_id} isCompanyJunior={application.isCompanyJunior} />
        </div>

        {application.attendance?.resolution === AttendanceResolution.OK ? (
          <div className="flex items-center gap-10">
            <div className="flex gap-6 w-48">
              <TyInput
                min="0"
                type="number"
                step={0.01}
                mask={(value: string) => Number(value).toFixed(2)}
                lang="en"
                className="w-[90px]"
                name="resolved_confirmed_time"
                suffix="h"
                initialValue={
                  (
                    Number(application.attendance.confirmed_time) +
                    Number(application.attendance.confirmed_credit_time) +
                    Number(application.attendance.confirmed_overtime)
                  ) / 60
                }
                disabled
              />
              <TyInput
                min="0"
                type="number"
                step={0.01}
                mask={(value: string) => Number(value).toFixed(2)}
                lang="en"
                className="w-[75px]"
                name="resolved_break"
                suffix="h"
                initialValue={
                  calculateDurationFromWorkTime(
                    application.attendance.confirmed_time * 60000,
                    false,
                    'hours'
                  ) -
                  application.attendance.confirmed_time / 60
                }
                disabled
              />
            </div>
          </div>
        ) : (
          <div className="flex gap-2 items-center text-red-500 text-caption-xl font-semibold">
            <ContentIcon name="block" className="size-[18px]" />
            {application.attendance && (
              <span>
                {
                  AttendanceResolutionDisplay[
                    application.attendance?.resolution
                  ]
                }
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-self-end items-center">
          <div className="flex gap-1 items-center">
            {application.attendance?.job_evaluation ===
              GOOD_JOB_EVALUATION_SCORE && <ThumbsUp checked />}
            {application.attendance?.job_evaluation ===
              BAD_JOB_EVALUATION_SCORE && <ThumbsDown checked />}
            <span>
              {moment(application.attendance?.created_at).format('DD.MM.YYYY')}
            </span>
            <span>{application.attendance?.confirmedBy?.first_name} {application.attendance?.confirmedBy?.last_name}</span>
          </div>
          {isLoading ? (
            <Spinner className="size-6" />
          ) : (
            <button
              className={classNames(
                'rounded-full border border-red-300 size-6 p-0 flex justify-center',
                'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
              )}
              onClick={() => onDeleteAttendance(application.attendance?.id)}
            >
              <ActionIcon name="delete_outline" className="size-4 " />
            </button>
          )}
          <button
            className={classNames(
              'relative rounded-full border border-red-300 size-6 p-0 flex justify-center',
              'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
            )}
            onClick={() => setOpenMenu(!openMenu)}
          >
            <FavouriteIcon name="more_vert" className="size-4 " />
            {openMenu && (
              <DropDownMenu
                className="-translate-x-[9rem] translate-y-11"
                options={menuOptions}
                onBlur={() => setOpenMenu(false)}
              />
            )}
          </button>
        </div>
      </div>
      <Modal
        isOpen={modalOpen}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        ariaHideApp={false}
        className="fixed top-[25%] right-[25%] w-[50%] max-h-[70%] bg-bg rounded-lg drop-shadow-lg z-50 flex flex-col"
        overlayClassName="bg-overlay fixed inset-0 z-modal"
      >
        <div className="text-secondary-600 text-heading-4-xl p-4 flex align-center border-b border-secondary-100">
          Ukončit spolupráci
          <FavouriteIcon
            name="close"
            onClick={() => setModalOpen(false)}
            className="size-5 cursor-pointer ml-auto"
          />
        </div>
        <BlockTymber
          applicationId={application.id}
          setModalOpen={setModalOpen}
        />
      </Modal>
    </Scope>
  );
};

export default ResolvedAttendanceRow;
